import ReplayRecordingAggregation from '@/session-replay/aggregations/replay-recording.aggregation';
import { runAggregationOnce } from '@/utils/aggregation';
import { getFrontendVersion } from '@/utils/frontend-version';
import { b64EncodeUnicode } from '@/utils/http';
import { convertConfigToTimeSeries } from '@/common/time-series-picker/time-series-picker.util.js';
import { convertTimeSeriesForAggregation } from '@/utils/date-range.utils';
import moment, { DATE_FORMAT } from '@/utils/moment-wrapper';
import { PendoNotification } from '@pendo/components-vue3';
import { http } from '@pendo/http';
import { updateFeatureIgnoredFrustrations } from '@/stores/features.utils';
import { i18n_t as $t } from '@/utils/i18n';
import get from 'lodash/get';
import invoke from 'lodash/invoke';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import * as Utils from './common.util';

export const REPLAY_SELECTED_EVENTS_KEY = '_pendo_sr_selected_events';

// Used for watch replay buttons as time series are not configurable
export const NON_CONFIGURABLE_DAYS = 31;

export const DEFAULT_CLIP_RETENTION_DAYS = 366;

export const MAX_CLIP_DESCRIPTION_LENGTH = 560;

export const TIME_SERIES_OUT_OF_RANGE_STATES = {
    partial: 'partial',
    full: 'full',
    none: 'none',
};

export const DEFAULT_TIME_SERIES = {
    config: {
        type: 'Today',
    },
    spec: {
        count: 1,
        period: 'dayRange',
    },
    label: 'Today',
};

export const ACCOUNT_METRICS_AGG_PARAMS = {
    timeSeries: {
        period: 'dayRange',
        first: 'dateAdd(startOfPeriod("daily",now()),-30,"days")',
        count: 30,
    },
    appId: 'expandAppIds("*")',
    blacklist: 'apply',
    includeAnonymous: true,
};

export const VISITOR_METRICS_AGG_PARAMS = {
    timeSeries: {
        period: 'dayRange',
        first: 'dateAdd(startOfPeriod("daily",now()),-30,"days")',
        count: 30,
    },
    appId: 'expandAppIds("*")',
};

export const FRUSTRATION_TYPES = ['deadClicks', 'errorClicks', 'rageClicks', 'uTurns'];

function getReplayBaseUrl (appId) {
    return `api/session-replay/s/_SID_/application/${appId}`;
}

export function defaultTimeSeriesWithRetentionDays (replayRetentionDays) {
    return {
        config: {
            type: `Last ${replayRetentionDays - 1} Days (and today)`,
        },
        spec: {
            count: replayRetentionDays,
            period: 'dayRange',
        },
    };
}

/** @returns {{ intersectStart: object, intersectEnd: object } | null} */
function computeCalendarIntersectionWithReplayRetention (replayRetentionDays, rangeStart, rangeLastInclusive) {
    if (!rangeStart?.isValid() || !rangeLastInclusive?.isValid()) {
        return null;
    }

    const retentionFirstDay = moment(undefined, { ignoreTimeZone: true })
        .startOf('day')
        .subtract(replayRetentionDays - 1, 'days');

    const todayStart = moment(undefined, { ignoreTimeZone: true }).startOf('day');

    if (rangeLastInclusive.isBefore(retentionFirstDay, 'day')) {
        return null;
    }

    const intersectStart = rangeStart.isBefore(retentionFirstDay, 'day')
        ? retentionFirstDay.clone()
        : rangeStart.clone();
    const intersectEnd = rangeLastInclusive.isAfter(todayStart, 'day')
        ? todayStart.clone()
        : rangeLastInclusive.clone();

    if (intersectStart.isAfter(intersectEnd, 'day')) {
        return null;
    }

    return { intersectStart, intersectEnd };
}

/**
 * Pipeline `timeSeries` for session replay aggregations: calendar overlap of the report range with replay retention.
 * Unchanged ranges use {@link convertTimeSeriesForAggregation} so rolling `now()` specs stay aligned with the app.
 *
 * AI agent replay merges expand recordingMetadata start by one day (`expandRecordingMetadataTimeSeriesForSessionBoundary`
 * in ai-agent-replay-helpers.ts) so sessions that began just before this window still resolve.
 *
 * @param {number} replayRetentionDays
 * @param {{ config: object, spec?: object, label?: string }} engageTimeSeries - Report / picker time series (e.g. selectedTimeSeries)
 * @returns {object | null} `dayRange` aggregation time series, or null when there is no overlap with retention
 */
export function narrowAggTimeSeriesForSessionReplayRetention (replayRetentionDays, engageTimeSeries) {
    if (engageTimeSeries == null || !get(engageTimeSeries, 'config.type')) {
        return null;
    }
    if (replayRetentionDays == null || Number.isNaN(Number(replayRetentionDays))) {
        return convertTimeSeriesForAggregation(engageTimeSeries);
    }

    let normalized;
    try {
        normalized = convertConfigToTimeSeries(engageTimeSeries.config);
    } catch {
        return null;
    }

    const rangeStart = moment(normalized.config.start, { ignoreTimeZone: true }).startOf('day');
    const rangeExclusiveEnd = moment(normalized.config.end, { ignoreTimeZone: true }).startOf('day');
    const rangeLastInclusive = rangeExclusiveEnd.clone().subtract(1, 'days');

    if (!rangeLastInclusive.isValid() || !rangeStart.isValid()) {
        return null;
    }

    const intersection = computeCalendarIntersectionWithReplayRetention(
        replayRetentionDays,
        rangeStart,
        rangeLastInclusive,
    );
    if (intersection == null) {
        return null;
    }

    const { intersectStart, intersectEnd } = intersection;

    if (intersectStart.isSame(rangeStart, 'day') && intersectEnd.isSame(rangeLastInclusive, 'day')) {
        return convertTimeSeriesForAggregation(engageTimeSeries);
    }

    const count = intersectEnd.diff(intersectStart, 'days') + 1;

    return {
        period: 'dayRange',
        first: `date("${intersectStart.format(DATE_FORMAT.iso)}")`,
        count,
    };
}

export function getRecordingEvents ({ appId, visitorId, recordingIds, minBrowserTime, maxBrowserTime }, abortSignal) {
    const b64VisitorId = `b64_${b64EncodeUnicode(visitorId)}`;

    const params = {
        recordingIdMap: true,
    };

    return http.post(
        `/${getReplayBaseUrl(appId)}/${b64VisitorId}`,
        {
            recordingIds,
            minBrowserTime,
            maxBrowserTime,
        },
        {
            headers: {
                'X-Pendo-Base64-Encoded-Params': true,
            },
            signal: abortSignal,
            params,
        },
    );
}

export function getReplaySettings ({ appId }) {
    return http.get(`/api/session-replay/s/_SID_/application/${appId}/settings`).then((res) => res.data);
}

export function updateReplaySettings ({ appId, replaySettings }) {
    return http
        .put(`/api/session-replay/s/_SID_/application/${appId}/settings`, replaySettings)
        .then((res) => res.data);
}

export function updateReplaySettingsFlag ({ appId, flag, set }) {
    return http
        .put(`/api/session-replay/s/_SID_/application/${appId}/settings/flag/${flag}`, { set })
        .then((res) => res.data);
}

export function buildReplayRouteObject ({ savedFilterId = 'lastSavedFilterId' } = {}) {
    const versionQueryParam = getFrontendVersion(window.location.href);
    const route = {
        name: 'ReplayLibrarySavedFilter',
        params: { savedFilterId },
        query: {},
    };
    if (versionQueryParam) {
        route.query.version = versionQueryParam;
    }

    return route;
}

export function getRecordingUrlParams ({
    visitorId,
    appId,
    startTime,
    endTime,
    minBrowserTime,
    maxBrowserTime,
    crossTabSessions,
    startingPageId,
} = {}) {
    const params = {
        visitorId,
        appId,
        startTime,
        endTime,
        minBrowserTime,
        maxBrowserTime,
        ...(startingPageId && { startingPageId }),
    };

    if (visitorId) {
        params.visitorId = decodeURIComponent(visitorId);
    }

    if (crossTabSessions) {
        params.crossTabSessions = decodeURIComponent(crossTabSessions).split(',');
    }

    return params;
}

/**
 * Creates a Replay Recording route object
 * @param {Object} replayContext - contains all of the required Recording url props (see `getRecordingUrlParams`)
 * @param {String} [visitorIdSearch] - used to persist visitor id search in the Replay Library
 * @returns {Object} Replay Recording route object
 */
export function buildReplayRecordingRouteObject ({
    replayContext = {},
    visitorIdSearch = '',
    dashboardId = '',
    contextId = '',
    queueIds = [],
    name = 'SessionReplayRecording',
    // Extra query params merged into the route, e.g. { openSummary: 'true' } to deep-link the summary panel
    additionalQueryParams = {},
}) {
    try {
        if (!replayContext.recordingSessionId) throw new Error('Missing recordingSessionId');

        const versionQueryParam = getFrontendVersion(window.location.href);
        const recordingUrlParams = Utils.getRecordingUrlParams(replayContext);
        const queryParams = {
            ...recordingUrlParams,
            // We need to manually encode ' to %27 because angular router will blow up if it sees a ' in the url
            visitorId: encodeURIComponent(recordingUrlParams.visitorId).replace(/'/g, '%27'),
            ...(dashboardId && { dashboardId }),
            ...(contextId && { contextId }),
            ...(visitorIdSearch && { visitorIdSearch: encodeURIComponent(visitorIdSearch).replace(/'/g, '%27') }),
            ...(versionQueryParam && { version: versionQueryParam }),
            ...(queueIds.length > 0 && { queueIds: queueIds.join(',') }),
            ...additionalQueryParams,
        };

        // We don't want to route to cross tab mode by default
        delete queryParams.crossTabSessions;

        return {
            name,
            params: {
                recordingSessionId: replayContext.recordingSessionId,
            },
            query: queryParams,
        };
    } catch (error) {

        console.error('Failed to build Replay recording route: ', error);
    }
}

export function buildReplaySummaryShareUrl ({ replayContext = {}, router } = {}) {
    if (!replayContext.recordingSessionId || !router) return '';

    // openSummary is read by the player to auto-open the summary panel (APP-156371)
    const route = buildReplayRecordingRouteObject({ replayContext, additionalQueryParams: { openSummary: 'true' } });
    if (!route) return '';

    const resolved = router.resolve(route);

    return resolved?.href ? `${window.location.origin}${resolved.href}` : '';
}

export function buildReplaySummaryClipboardText ({ title = '', timelineItems = [], accountId = '', url = '', visitorLabel = '' } = {}) {
    const lines = [];

    if (url) lines.push(url);

    if (visitorLabel) lines.push(`${$t('common.Visitor ID')}: ${visitorLabel}`);
    if (accountId) lines.push(`${$t('common.Account ID')}: ${accountId}`);

    if (timelineItems.length > 0) {
        lines.push('');

        if (title) lines.push(title);

        lines.push($t('aiSummaryPanel.Key moments'));

        timelineItems.forEach(({ offsetLabel, description }) => {
            lines.push(`${offsetLabel} ${description}`);
        });
    }

    return lines.join('\n');
}

export function sendTrackEvent (name, data, subId) {
    invoke(window, 'pendo.track', name, { ...data, subId });
}

export function getEntityKey (replay) {
    if (replay.recordingSessionId) {
        return replay.recordingSessionId;
    }

    return generateOldEntityKey(replay);
}

export function generateOldEntityKey (replay) {
    const recordingId = get(replay, 'recordingIds[0]', '');

    return `${replay.visitorId}_${recordingId}_${replay.accountId}`;
}

export function withinRetentionDays (date, replayRetentionDays) {
    // BE adds +1 to the "advertised retention period"
    // when setting recordingRetentionDays to account for timezone differences
    // which is why FE has to do the -1 to recordingRetentionDays.
    const now = moment().valueOf();

    const midnightTodaySubTz = moment(now)
        .startOf('day')
        .valueOf();
    const retentionPeriodStart = midnightTodaySubTz - 86400000 * (replayRetentionDays - 1);

    return date >= retentionPeriodStart && date <= now;
}

/**
 * @typedef {Object} inactivityPeriod
 * @property {Number} inactivityStart - timestamp when a inactive period starts
 * @property {Number} inactivityEnd -  timestamp when a inactive period ends
 */

/**
 * @typedef {Object} chunk
 * @property {String} recordingId
 * @property {Number} chunkStartTime - timestamp when a recordingId starts
 * @property {Number} chunkEndTime - timestamp when a recordingId ends
 */

/**
 * @typedef {Object} RecordingInfo - data about a specific recordingSessionId
 * @property {String} visitorId
 * @property {String} accountId
 * @property {String} tabId
 * @property {Number} appId
 * @property {String[]} recordingIds - array of recording IDs
 * @property {Number} startTime - timestamp of the first rrweb event associated with a recordingSessionId
 * @property {Number} endTime - timestamp of the last rrweb event associated with a recordingSessionId
 * @property {Number} minBrowserTime - minBrowserTime is a timestamp that has been corrected during event
 * ingestion. It represents the beginning of the session relative to other pendo events. A difference between startTime
 * and minBrowserTime represents an error in a user's system time during recording; this delta can be used to align
 * rrweb events and pendo events.
 * @property {chunk[]} chunks
 * @property {inactivityPeriod[]} inactivityPeriods
 */

export async function runReplayRecordingAggregation (replayContext, replayRetentionDays) {
    let { startTime, endTime, maxBrowserTime, minBrowserTime, startTimeFromFunnel, endTimeFromFunnel } = replayContext;
    if (startTimeFromFunnel && endTimeFromFunnel) {
        startTime = startTimeFromFunnel;
        endTime = endTimeFromFunnel;
    } else if (minBrowserTime && maxBrowserTime) {
        startTime = minBrowserTime;
        endTime = maxBrowserTime;
    }

    const value = await runAggregationOnce(ReplayRecordingAggregation, {
        ...replayContext,
        startTime,
        endTime,
        replayRetentionDays,
    });

    if (value.length === 0) return;

    return {
        ...replayContext,
        ...Utils.handleReplayRecordingAggValue(value),
    };
}

export function handleReplayRecordingAggValue (value) {
    let replayRecordings = value;

    // The non cross-tab replay agg returns one row with aggregate data common to the recordingSessionId.
    const aggregateRowIndex = replayRecordings.findIndex((row) => !isUndefined(row.recordingIds));
    const [aggregateRow] = replayRecordings.splice(aggregateRowIndex, 1);
    const {
        visitorId,
        accountId,
        tabId,
        appId,
        startTime,
        endTime,
        minBrowserTime,
        maxBrowserTime,
        recordingIds,
        recordingSize,
    } = aggregateRow;
    let { inactivityPeriods } = aggregateRow;
    // The inactivityPeriods operator returns null if no inactivity is found
    // The inactivityPeriods operator returns inactivity per session for cross tab so
    // it's not defined on the aggregate row
    if (isNull(inactivityPeriods) || isUndefined(inactivityPeriods)) {
        inactivityPeriods = [];
    }

    const mapInactivities = (inactivityPeriods) => {
        return inactivityPeriods.map((inactivityPeriod) => {
            return {
                inactivityStart: inactivityPeriod.start - startTime,
                inactivityEnd: inactivityPeriod.end - startTime,
            };
        });
    };
    inactivityPeriods = mapInactivities(inactivityPeriods);

    // The non cross-tab replay agg returns rows with aggregated activityTimelineTimestamps
    // each representing data for individual recordingSessionId (aggregatedSessionActivities).
    // All subsequent rows represent data for individual recordingIds
    // that are associated with a recordingSessionId (replayRecordings).
    const sessionActivities = [];
    const aggregatedSessionActivities = replayRecordings.filter((row) => row.activityTimelineTimestamps);
    replayRecordings = replayRecordings.filter((row) => !row.activityTimelineTimestamps);
    const sessionNumberMap = new Map(); // map to link recordingSessionId to sessionNumber
    let sessionRouteObjects; // each cross tab session's route obj

    // We need to map an individual recordingId (chunk) to each activityTimelineTimestamp
    // but only if we have multiple sessions
    if (aggregatedSessionActivities.length > 1) {
        const sessionActivityTimestamps = [];
        sessionRouteObjects = {};
        aggregatedSessionActivities.forEach((session, sessionNumber) => {
            sessionNumberMap.set(session.recordingSessionId, sessionNumber);
            sessionRouteObjects[session.recordingSessionId] = buildReplayRecordingRouteObject({
                replayContext: session,
            });
            session.activityTimelineTimestamps.forEach((timestamp) => {
                sessionActivityTimestamps.push({
                    timestamp,
                    sessionNumber,
                });
                replayRecordings.forEach((recording) => {
                    if (session.recordingSessionId !== recording.recordingSessionId) {
                        return;
                    }

                    const timestampOccurredInChunk =
                        timestamp >= recording.chunkStartTime && timestamp <= recording.chunkEndTime;
                    if (timestampOccurredInChunk) {
                        sessionActivities.push({
                            recordingId: recording.recordingId,
                            recordingSessionId: recording.recordingSessionId,
                            activityOffset: timestamp - startTime,
                            chunkEndTime: recording.chunkEndTime,
                            chunkStartTime: recording.chunkStartTime,
                            sessionNumber,
                        });
                    }
                });
            });
        });
        inactivityPeriods = getCrossTabInactivityPeriods(
            aggregatedSessionActivities,
            sessionActivityTimestamps,
            startTime,
            endTime,
        );
    }
    // Ensure chronological list of timestamps for when a session receives activity
    sessionActivities.sort((a, b) => a.activityOffset - b.activityOffset);

    return {
        visitorId,
        accountId,
        tabId,
        appId,
        recordingIds,
        startTime,
        endTime,
        minBrowserTime,
        maxBrowserTime,
        chunks: replayRecordings,
        inactivityPeriods,
        sessionActivities,
        sessionNumberMap,
        recordingSize,
        ...(sessionRouteObjects && { sessionRouteObjects }),
    };
}

function getCrossTabInactivityPeriods (sessions, sessionActivityTimestamps, sessionStart, sessionEnd) {
    if (sessionActivityTimestamps.length === 0) return [];
    const inactivityPeriods = [];

    sessionActivityTimestamps.sort((a, b) => a.timestamp - b.timestamp);
    // if first activity timestamp is greater than the session start, the session starts with inactivity
    if (sessionActivityTimestamps[0].timestamp > sessionStart) {
        inactivityPeriods.push({
            inactivityStart: 0,
            inactivityEnd: sessionActivityTimestamps[0].timestamp - sessionStart,
        });
    }
    // build inactivity by looking at which tab is active at each point in the replay
    sessionActivityTimestamps.forEach(({ timestamp, sessionNumber }, activityIndex) => {
        const session = sessions[sessionNumber];
        const nextActivityTimestamp = sessionActivityTimestamps[activityIndex + 1];
        const previousSessionActivityTimestamp =
            activityIndex > 0 ? sessionActivityTimestamps[activityIndex - 1] : undefined;
        const previousSession = sessions[previousSessionActivityTimestamp?.sessionNumber];
        // if previous session ended. The time between that session and the next session should be inactivity
        if (previousSession && previousSession.endTime < timestamp) {
            inactivityPeriods.push({
                inactivityStart: previousSession.endTime - sessionStart,
                inactivityEnd: timestamp - sessionStart,
            });
        }
        if (session.inactivityPeriods) {
            // if there is no next active session we can use the rest of this session's inactivity
            if (!nextActivityTimestamp) {
                for (let i = 0; i < session.inactivityPeriods.length; i++) {
                    const inactivity = session.inactivityPeriods[i];
                    const inactivityAfterActiveTimestamp = inactivity.end > timestamp;
                    if (inactivityAfterActiveTimestamp) {
                        inactivityPeriods.push({
                            inactivityStart: Math.max(inactivity.start, timestamp) - sessionStart,
                            inactivityEnd: inactivity.end - sessionStart,
                        });
                    }
                }
                // if the end of this session is not the end of the replay then it finishes
                // with inactivity in another tab
                if (session.endTime < sessionEnd) {
                    inactivityPeriods.push({
                        inactivityStart: session.endTime - sessionStart,
                        inactivityEnd: sessionEnd - sessionStart,
                    });
                }
            }
            if (nextActivityTimestamp) {
                const nextSessionTimestamp = nextActivityTimestamp.timestamp;
                for (let i = 0; i < session.inactivityPeriods.length; i++) {
                    const inactivity = session.inactivityPeriods[i];
                    const inactivityAfterActiveTimestamp = inactivity.end > timestamp;
                    const inactivityStartBeforeNextActiveTimestamp = inactivity.start < nextSessionTimestamp;
                    if (inactivityAfterActiveTimestamp && inactivityStartBeforeNextActiveTimestamp) {
                        inactivityPeriods.push({
                            inactivityStart: Math.max(inactivity.start, timestamp) - sessionStart,
                            inactivityEnd: Math.min(inactivity.end, nextSessionTimestamp) - sessionStart,
                        });
                    }
                    if (inactivity.end >= nextSessionTimestamp) break;
                }
            }
        }
    });

    return inactivityPeriods;
}

export function getSelectedFields (columns = []) {
    const columnKeys = columns.map((column) => {
        if (column.schema === 'number' || column.schema === 'aggregation') {
            const name = column.prop;

            return { [name]: `if(isNil(${name}), 0, ${name})` };
        }

        return column.id;
    });

    return [
        'accountId',
        'appId',
        'app',
        'endTime',
        'eventCount',
        'guideIds',
        'startTime',
        'maxBrowserTime',
        'minBrowserTime',
        'visitorId',
        'tabId',
        'totalInactivity',
        'recordingSessionId',
        'rageClickCount',
        'deadClickCount',
        'detractorNpsCount',
        'errorClickCount',
        'uTurnCount',
        'overGuidance',
        'duration',
        'totalEventScore',
        'frustrationCountScore',
        'aiSummary',
        ...columnKeys,
    ];
}

export function getDashboardRoute (homeDashboardId, firstDashboardId) {
    const route = { name: 'Dashboard' };

    if (homeDashboardId) {
        route.params = { id: homeDashboardId };
    } else if (firstDashboardId) {
        route.params = { id: firstDashboardId };
    }

    return route;
}

export function getReplayContextById (contextId) {
    const url = `/api/session-replay/s/_SID_/urlcontext/${contextId}`;

    return http.get(url).then((res) => res.data);
}

export function createSessionReplayUrlContextEntity (urlContextBody) {
    return http
        .post(`/api/session-replay/s/_SID_/application/${urlContextBody.appId}/urlcontext`, urlContextBody)
        .then((res) => res.data.id);
}

export async function createSessionReplayUrlContext ({
    appId,
    accountId,
    recordingIds,
    recordingSessionId,
    visitorId,
    selectedEvents,
    frustration,
    insight,
}) {
    try {
        return await Utils.createSessionReplayUrlContextEntity({
            appId,
            accountId,
            recordingIds,
            recordingSessionId,
            visitorId,
            selectedEvents,
            frustration,
            insight,
        });
    } catch (error) {

        console.error(`Error while trying to create replay context - ${error}`);
    }
}

export function convertObjectArraysToSets (obj) {
    if (!obj) return obj;

    Object.keys(obj).forEach((key) => {
        if (Array.isArray(obj[key])) {
            obj[key] = new Set(obj[key]);
        } else if (typeof obj[key] === 'object' && obj[key] !== null) {
            convertObjectArraysToSets(obj[key]);
        }
    });

    return obj;
}

export function trackReplayNotFound ({ reason, page, appPlatform }) {
    window.pendo.track('replay-not-found', {
        // The reason why the status is CONTENT_STATE.EMPTY. In some cases, you could use the message
        // from a caught exception. If outside of a try/catch, you can use a string that describes why
        // the status is empty
        reason,
        // This will be the $route.name so we can map the track event back to a specific page in the
        // app. From this you can infer where the player was used (regular, overlay, clips) and in the
        // case of the overlay, what page overlay was opened over.
        page,
        appPlatform,
    });
}

export function registerServiceWorker (nav, options) {
    return nav.serviceWorker.register(
        new URL(/* webpackIgnore: true */ './service-worker/replay-service-worker.worker.js', import.meta.url),
        options,
    );
}

export function fetchReplayAiSummary ({ appId, recordingSessionId, visitorId, startTime, endTime, expectedBehavior }) {
    return http
        .post('/api/session-replay/s/_SID_/ai/jira', {
            appId,
            recordingSessionId,
            visitorId,
            startTime,
            endTime,
            expectedBehavior,
        })
        .then((res) => res.data);
}

export function triggerAiSummary ({ appId, visitorId, accountId, endTime, recordingIds, startTime, recordingSessionId }) {
    return http
        .post('/api/session-replay/s/_SID_/ai/summary', {
            appId,
            visitorId,
            accountId,
            endTime,
            recordingIds,
            startTime,
            recordingSessionId,
        })
        .then((res) => res.data);
}

export const filterAnnotatable = (event) => {
    return get(event, "fact['==']") !== 0;
};

export function buildConditionalEventsMap (selectedEvents) {
    return selectedEvents.filter(({ eventProperties }) => eventProperties?.length).reduce((acc, event) => {
        event.eventProperties.forEach(({ field, fact }) => {
            if (!fact) return;
            Object.entries(fact).forEach(([operator, value]) => {
                if (!acc[event.id]) {
                    acc[event.id] = [];
                }
                acc[event.id].push({
                    field,
                    operator,
                    value: JSON.stringify(value),
                });
            });
        });

        return acc;
    }, {});
}

export async function ignoreFeatureFrustration ({ featureId, ignoredFrustrationTypes, frustrationType }) {
    if (!featureId || !ignoredFrustrationTypes?.length) {
        return false;
    }

    const ignoredSuccessMessages = {
        rageClick: $t("sessionReplayIgnoreFrustration.This Feature won't be flagged as a rage click anymore. To undo, go to the Feature's details page."),
        deadClick: $t("sessionReplayIgnoreFrustration.This Feature won't be flagged as a dead click anymore. To undo, go to the Feature's details page."),
        errorClick: $t("sessionReplayIgnoreFrustration.This Feature won't be flagged as an error click anymore. To undo, go to the Feature's details page."),
    };

    try {
        await updateFeatureIgnoredFrustrations({
            id: featureId,
            ignoredFrustrationTypes,
        });

        PendoNotification({
            type: 'success',
            title: $t('sessionReplayIgnoreFrustration.Frustration ignored'),
            message: frustrationType ? ignoredSuccessMessages[frustrationType] || '' : '',
            duration: 3000,
        });

        return true;
    } catch {
        PendoNotification({
            type: 'error',
            title: $t('sessionReplayIgnoreFrustration.Unable to ignore frustration'),
            message: $t('sessionReplayIgnoreFrustration.Pendo encountered an error. Please try again.'),
            duration: 3000,
        });

        return false;
    }
}